import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Row, Col, Card, ListGroup, Badge, Button, Modal, Image, Form, Alert,
  OverlayTrigger, Tooltip
} from 'react-bootstrap';
import {
  InspectionsClient,
  VehiclesClient,
  InspectionItemsClient,
  CategorysClient,
  LeadsClient,
  LeadDto,
  DealersClient
} from '../web-api-client.ts';
import Loader from './Loader.js';
import BackButton from './BackButton.js';
import { convertUtcToLocal } from './utils/utils.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPrint, faCamera, faCircleCheck, faExclamationTriangle, faTimesCircle,
  faCircleQuestion, faXmark
} from '@fortawesome/free-solid-svg-icons';

type ItemStatus = 0 | 1 | 2 | 3; // 0=Pass, 1=Attention, 2=Urgent, 3=N/A
const statusMeta: Record<ItemStatus, { variant: string; icon: any; label: string; symbol: string }> = {
  0: { variant: 'success', icon: faCircleCheck, label: 'OK', symbol: '✓' },
  1: { variant: 'warning', icon: faExclamationTriangle, label: 'Needs Attention', symbol: '!' },
  2: { variant: 'danger', icon: faTimesCircle, label: 'Urgent', symbol: '×' },
  3: { variant: 'secondary', icon: faTimesCircle, label: 'N/A', symbol: '–' },
};

const statusBadge = (
  s: ItemStatus | undefined,
  statusLabelGreen: string | undefined,
  statusLabelYellow: string | undefined,
  statusLabelRed: string | undefined
) =>
  s === undefined ? null : (
    <Badge
      bg={statusMeta[s].variant}
      className="ms-2 status-badge"
      title={statusMeta[s].label}
    >
      <FontAwesomeIcon icon={statusMeta[s].icon} className="me-1" />
      {statusLabelGreen && s === 0 ? statusLabelGreen : statusLabelYellow && s === 1 ? statusLabelYellow : statusLabelRed && s === 2 ? statusLabelRed : ''}
    </Badge>
  );

type MasterItem = {
  id: number;
  name: string;
  categoryId?: number | null;
  categoryName?: string | null;
  displayOrder?: number | null;
  hasPhoto?: boolean | null;
  statusLabelGreen?: string | null;
  statusLabelYellow?: string | null;
  statusLabelRed?: string | null;
  description?: string | null;
};

type Category = { id: number; name: string };

type PhotoDto = {
  id: number;
  inspectionId: number;
  inspectionItemId: number;
  url: string;
  thumbnailUrl?: string | null;
  sortOrder?: number | null;
  created?: string;
};

type VideoDto = {
  id: number;
  inspectionId: number;
  inspectionItemId: number;
  url: string;
  thumbnailUrl?: string | null; // poster
  sortOrder?: number | null;
  created?: string;
  contentType?: string | null;
  durationMs?: number | null;
};

const Empty: React.FC<{ text: string }> = ({ text }) =>
  <div className="text-muted fst-italic">{text}</div>;

const ScoreLegend: React.FC<{ passed: number; attention: number; urgent: number }> = ({ passed, attention, urgent }) => (
  <ListGroup variant="flush" className="small score-legend">
    <ListGroup.Item className="px-0 d-flex justify-content-between">
      <span className="d-inline-flex align-items-center">
        <FontAwesomeIcon icon={faCircleCheck} className="me-2 text-success legend-icon legend-ok" /> Passed
      </span>
      <strong>{passed}</strong>
    </ListGroup.Item>
    <ListGroup.Item className="px-0 d-flex justify-content-between">
      <span className="d-inline-flex align-items-center">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning legend-icon legend-attn" /> Needs attention
      </span>
      <strong>{attention}</strong>
    </ListGroup.Item>
    <ListGroup.Item className="px-0 d-flex justify-content-between">
      <span className="d-inline-flex align-items-center">
        <FontAwesomeIcon icon={faTimesCircle} className="me-2 text-danger legend-icon legend-urgent" /> Urgent
      </span>
      <strong>{urgent}</strong>
    </ListGroup.Item>
  </ListGroup>
);

const DviViewer: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview");

  const [loading, setLoading] = useState(true);
  const [inspection, setInspection] = useState<any>(null);
  const [vehicle, setVehicle] = useState<any>(null);
  const [masterItems, setMasterItems] = useState<MasterItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<number | 'all'>('all');

  const [photoModal, setPhotoModal] = useState<{ open: boolean; url?: string }>({ open: false });
  const [videoModal, setVideoModal] = useState<{ open: boolean; url?: string; poster?: string | null }>(
    { open: false }
  );

  const [photosByItem, setPhotosByItem] = useState<Map<number, PhotoDto[]>>(new Map());
  const [videosByItem, setVideosByItem] = useState<Map<number, VideoDto[]>>(new Map());

  // Lead form state
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [helpful, setHelpful] = useState<null | 'yes' | 'no'>(null);
  const [comments, setComments] = useState('');
  const [sendMoreInfo, setSendMoreInfo] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);
  const [dealerInfo, setDealerInfo] = useState<any>(null);

  // Lead modal state (popup after 10 seconds)
  const [showLeadModal, setShowLeadModal] = useState(false);
  const leadModalDismissedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const dealersClient = new DealersClient();
        const inspClient = new InspectionsClient();
        const itemsClient = new InspectionItemsClient();
        const catsClient = new CategorysClient();
        const vehiclesClient = new VehiclesClient();

        const insp = await inspClient.getInspectionById(Number(id));
        const dealerInfo = await dealersClient.getDealerInfo(insp.dealerId || 0);
        setDealerInfo(dealerInfo);

        const [items, cats, veh] = await Promise.all([
          itemsClient.getInspectionItemList(insp.dealerId),
          catsClient.getCategoryList(insp.dealerId || 0),
          insp.vehicleId ? vehiclesClient.getVehicleById(insp.vehicleId) : Promise.resolve(null)
        ]);
        if (isPreview !== "true") inspClient.countView(Number(id));

        // ---- MEDIA LOAD ----
        // Photos (existing)
        let allPhotos: PhotoDto[] = [];
        const anyClient = inspClient as any;
        if (typeof anyClient.getInspectionPhotos === 'function') {
          allPhotos = await anyClient.getInspectionPhotos(Number(id));
        } else {
          const resp = await fetch(`/api/Inspections/${encodeURIComponent(String(id))}/photos`, {
            headers: { Accept: 'application/json' }
          });
          allPhotos = resp.ok ? await resp.json() : [];
        }

        // Videos (new)
        let allVideos: VideoDto[] = [];
        if (typeof (inspClient as any).getInspectionVideos === 'function') {
          allVideos = await (inspClient as any).getInspectionVideos(Number(id));
        } else {
          const resp = await fetch(`/api/Inspections/${encodeURIComponent(String(id))}/videos`, {
            headers: { Accept: 'application/json' }
          });
          allVideos = resp.ok ? await resp.json() : [];
        }

        if (cancelled) return;
        setInspection(insp);
        setMasterItems(
          (items ?? []).map((m: any) => ({
            id: m.id, name: m.name,
            categoryId: m.categoryId ?? null,
            categoryName: m.categoryName ?? m.category?.name ?? null,
            displayOrder: m.displayOrder ?? null,
            hasPhoto: m.hasPhoto ?? null,
            statusLabelGreen: m.statusLabelGreen ?? null,
            statusLabelYellow: m.statusLabelYellow ?? null,
            statusLabelRed: m.statusLabelRed ?? null,
            description: m.description ?? null,
          }))
        );
        const catList: Category[] = (cats ?? []).map((c: any) => ({ id: c.id, name: c.name }));
        setCategories(catList);
        setVehicle(veh);

        // Index media by item
        const photoMap = new Map<number, PhotoDto[]>();
        for (const p of allPhotos) {
          const arr = photoMap.get(p.inspectionItemId) ?? [];
          arr.push(p);
          photoMap.set(p.inspectionItemId, arr);
        }
        for (const [k, arr] of photoMap.entries()) {
          arr.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id);
        }
        setPhotosByItem(photoMap);

        const videoMap = new Map<number, VideoDto[]>();
        for (const v of allVideos) {
          const arr = videoMap.get(v.inspectionItemId) ?? [];
          arr.push(v);
          videoMap.set(v.inspectionItemId, arr);
        }
        for (const [k, arr] of videoMap.entries()) {
          arr.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id);
        }
        setVideosByItem(videoMap);

      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (id) load();
    return () => { cancelled = true; };
  }, [id, isPreview]);

  // Popup lead modal after 10 seconds on-page (screen only)
  useEffect(() => {
    if (loading) return;
    if (!inspection) return;
    if (isPreview === "true") return;
    if (leadSubmitted) return;
    if (leadModalDismissedRef.current) return;

    const t = window.setTimeout(() => {
      if (!leadModalDismissedRef.current && !leadSubmitted) {
        setShowLeadModal(true);
      }
    }, 10_000);

    return () => window.clearTimeout(t);
  }, [loading, inspection, isPreview, leadSubmitted]);

  const closeLeadModal = () => {
    setShowLeadModal(false);
    leadModalDismissedRef.current = true;
  };

  const openLeadModal = () => {
    setShowLeadModal(true);
  };

  const items = useMemo(() => {
    if (!inspection) return [];
    const miById = new Map(masterItems.map((m) => [m.id, m]));
    const records = (inspection.inspectionItemRecords ?? []).filter((r: any) => r.status !== 3);

    const vm = records.map((r: any) => {
      const m = miById.get(r.inspectionItemId);
      return {
        id: r.id,
        inspectionItemId: r.inspectionItemId,
        displayOrder: m?.displayOrder ?? 0,
        name: m?.name ?? `Item ${r.inspectionItemId}`,
        categoryId: m?.categoryId ?? undefined,
        categoryName: m?.categoryName ?? undefined,
        status: r.status as ItemStatus,
        technicianNote: r.note ?? undefined,
        recommendation: r.recommendation ?? undefined,
        statusLabelGreen: m?.statusLabelGreen ?? undefined,
        statusLabelYellow: m?.statusLabelYellow ?? undefined,
        statusLabelRed: m?.statusLabelRed ?? undefined,
        description: m?.description ?? undefined,
        photos: photosByItem.get(r.inspectionItemId) ?? [],
        videos: videosByItem.get(r.inspectionItemId) ?? [],
      };
    });

    vm.sort((a, b) => a.displayOrder - b.displayOrder);
    return vm;
  }, [inspection, masterItems, photosByItem, videosByItem]);

  const derivedCategories = useMemo(() => {
    if (categories.length) return categories;
    const map = new Map<number, string>();
    items.forEach(i => {
      if (i.categoryId && i.categoryName && !map.has(i.categoryId)) {
        map.set(i.categoryId, i.categoryName);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [categories, items]);

  const filteredItems = useMemo(() => {
    if (activeCategoryId === 'all') return items;
    return items.filter(i => i.categoryId === activeCategoryId);
  }, [items, activeCategoryId]);

  const score = useMemo(() => {
    const passed = items.filter(i => i.status === 0).length;
    const attention = items.filter(i => i.status === 1).length;
    const urgent = items.filter(i => i.status === 2).length;
    return { passed, attention, urgent };
  }, [items]);

  if (loading) return <Loader />;

  if (!inspection) {
    return (
      <div className="container py-3">
        <BackButton />
        <Card className="mt-3"><Card.Body>Inspection not found.</Card.Body></Card>
      </div>
    );
  }

  const vehicleTitle = vehicle
    ? [vehicle.year, vehicle.makeName, vehicle.modelName].filter(Boolean).join(' ')
    : (inspection.vehicleLabel || '');

  const inspectionDateLocal = inspection?.inspectionDate
    ? convertUtcToLocal(inspection.inspectionDate)
    : null;

  const formatMiles = (price: string): string => {
    let num: number = +price;
    if (num < 1000) {
      return (num.toString())
    }
    if ((num / 1000) < 1000) {
      return (Math.floor(num / 1000).toString().concat(",") + ((num % 1000).toString().padStart(3, '0')))
    }
    else {
      return (formatMiles(Math.floor(num / 1000).toString()).concat(",") + ((num % 1000).toString().padStart(3, '0')))
    }
  }

  const handleLeadSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLeadError(null);

    const emailOk = /^\S+@\S+\.\S+$/.test(leadEmail);
    if (!leadName.trim()) return setLeadError('Please enter your name.');
    if (!emailOk) return setLeadError('Please enter a valid email address.');
    if (!helpful) return setLeadError('Please let us know if this was helpful.');

    try {
      setLeadSubmitting(true);
      const leadsClient = new LeadsClient();
      const dto: LeadDto = {
        dealerId: inspection.dealerId,
        inspectionId: inspection.id,
        vehicleId: inspection.vehicleId ?? undefined,
        name: leadName.trim(),
        email: leadEmail.trim(),
        isHelpful: helpful === 'yes' as any,
        message: (comments.trim() || undefined) as any,
        contactMe: sendMoreInfo as any,
        phone: (leadPhone.trim() || undefined) as any,
      };
      await leadsClient.createLead(dto);

      setLeadSubmitted(true);

      // Clear fields
      setLeadName('');
      setLeadEmail('');
      setHelpful(null);
      setComments('');
      setSendMoreInfo(false);
      setLeadPhone('');

      // Close modal if it's open
      setShowLeadModal(false);
      leadModalDismissedRef.current = true;
    } catch (err) {
      console.error(err);
      setLeadError('Something went wrong while submitting your request. Please try again.');
    } finally {
      setLeadSubmitting(false);
    }
  };

  // IMPORTANT: render function (not a nested component) to avoid focus loss
  const renderLeadForm = (compact = false) => (
    <>
      {leadSubmitted && (
        <Alert variant="success" className="mb-3">
          Thanks! We’ve received your request. We’ll reach out shortly.
        </Alert>
      )}

      {leadError && (
        <Alert variant="danger" className="mb-3">
          {leadError}
        </Alert>
      )}

      {!leadSubmitted && (
        <Form onSubmit={handleLeadSubmit}>
          <Row className={compact ? "g-2" : "g-3"}>
            <Col md={12}>
              <Form.Label className="fw-semibold">Vehicle</Form.Label>
              <div className="text-muted">
                {vehicleTitle || inspection.vehicleVin || '—'}
              </div>
            </Col>

            <Col md={4}>
              <Form.Group controlId={compact ? "leadNameModal" : "leadName"}>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  disabled={leadSubmitting}
                  value={leadName}
                  onChange={(e) => setLeadName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId={compact ? "leadEmailModal" : "leadEmail"}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  disabled={leadSubmitting}
                  type="email"
                  value={leadEmail}
                  onChange={(e) => setLeadEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId={compact ? "leadPhoneModal" : "leadPhone"}>
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  disabled={leadSubmitting}
                  type="tel"
                  value={leadPhone}
                  onChange={(e) => setLeadPhone(e.target.value)}
                  placeholder="(123) 456-7890"
                  required
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Label className="d-block">Was this helpful?</Form.Label>
              <div className="d-flex gap-3 flex-wrap">
                <Form.Check
                  disabled={leadSubmitting}
                  inline
                  id={compact ? "helpful-yes-modal" : "helpful-yes"}
                  type="radio"
                  name={compact ? "helpful-modal" : "helpful-bottom"}
                  label="Yes"
                  checked={helpful === 'yes'}
                  onChange={() => setHelpful('yes')}
                />
                <Form.Check
                  inline
                  disabled={leadSubmitting}
                  id={compact ? "helpful-no-modal" : "helpful-no"}
                  type="radio"
                  name={compact ? "helpful-modal" : "helpful-bottom"}
                  label="No"
                  checked={helpful === 'no'}
                  onChange={() => setHelpful('no')}
                />
              </div>
            </Col>

            <Col md={12}>
              <Form.Group controlId={compact ? "leadCommentsModal" : "leadComments"}>
                <Form.Label>Comments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={compact ? 3 : 4}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Tell us anything we should know..."
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Check
                id={compact ? "send-more-info-modal" : "send-more-info"}
                disabled={leadSubmitting}
                type="checkbox"
                label="Send me more info about this vehicle"
                checked={sendMoreInfo}
                onChange={(e) => setSendMoreInfo(e.target.checked)}
              />
            </Col>
          </Row>

          <div className="mt-3 d-flex justify-content-end gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={closeLeadModal}
            >
              Close
            </Button>

            <Button type="submit" variant="primary" disabled={leadSubmitting}>
              {leadSubmitting ? 'Submitting…' : 'Submit'}
            </Button>
          </div>
        </Form>
      )}
    </>
  );

  return (
    <div className="container py-3 dvi-viewer-root">
      {/* Screen header */}
      <div className="d-flex align-items-center justify-content-between mb-3 print-hidden">
        <img
          className="logo print-hidden"
          src="logo-light.png"
          alt="Logo"
          style={{ maxWidth: '150px', height: 'auto', objectFit: 'contain', marginRight: '10px' }}
        />

        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => navigate(0)}>Refresh</Button>
          <Button variant="primary" onClick={() => window.print()}>
            <FontAwesomeIcon icon={faPrint} className="me-2" /> Print
          </Button>
        </div>
      </div>

      {/* Print header */}
      <div className="print-only print-header mb-3">
        <div className="d-flex align-items-start justify-content-between">
          <div className="d-flex gap-3" style={{ width: '100%' }}>
            <div style={{ width: '40%', maxWidth: '320px' }} className="print-vehicle-photo">
              <div className="photo-wrap">
                {vehicle?.photoUri ? (
                  <img src={vehicle.photoUri} alt="Vehicle" />
                ) : (
                  <div className="photo-empty">No vehicle image</div>
                )}
              </div>
            </div>
            <div className="flex-grow-1">
              <img src="logo-dark.png" alt="Logo" className="print-logo" />
              <h2 className="mb-1">{vehicleTitle || 'Vehicle'}</h2>
              <div className="text-muted small">
                VIN: {vehicle?.vin || inspection.vehicleVin || '—'}
              </div>
              <div className="text-muted small">
                {/* Miles: {vehicle?.mileage || '—'} */}
                Miles: {(vehicle?.mileage != null) ? formatMiles((vehicle?.mileage).toString()) : '-'}
              </div>
              <div className="text-muted small">
                Price: {vehicle?.price != null ? `$${Number(vehicle.price).toLocaleString('en-US')}` : '-'}
              </div>
              {inspectionDateLocal && (
                <div className="text-muted small">
                  Inspection Date: {inspectionDateLocal}
                </div>
              )}

              <div className="mt-3">
                <h6 className="mb-1">Inspection Score</h6>
                <ScoreLegend passed={score.passed} attention={score.attention} urgent={score.urgent} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Row className="screen-grid">
        {/* LEFT */}
        <Col lg={4} className="mb-3 print-hidden">
          <Card className="mb-3">
            <Card.Body>
              <div className="bg-light mb-3 d-flex align-items-center justify-content-center"
                style={{ width: '100%', aspectRatio: '4/3', borderRadius: 8 }}>
                {vehicle?.photoUri ? (
                  <img
                    src={vehicle.photoUri}
                    alt="Vehicle"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid rgba(0,0,0,0.1)",
                    }}
                  />
                ) : (
                  <div className="text-muted small">No vehicle image</div>
                )}
              </div>

              <Row>
                <Col md={6} className="mobile-center">
                  {vehicleTitle
                    ? <div className="fw-semibold">{vehicleTitle}</div>
                    : <Empty text="No vehicle details" />}
                  <div className="text-muted small">
                    VIN: {vehicle?.vin || inspection.vehicleVin || '—'}
                  </div>
                  <div className="text-muted small">
                    {/* Miles: {vehicle?.mileage || '—'} */}
                    Miles: {(vehicle?.mileage != null) ? formatMiles((vehicle?.mileage).toString()) : '-'}
                  </div>
                  <div className="text-muted small">
                    Price: {vehicle?.price != null ? `$${Number(vehicle.price).toLocaleString('en-US')}` : '-'}
                  </div>
                </Col>
                <Col md={6} className="mobile-center">
                  {dealerInfo && (
                    <img
                      className="logo"
                      src={dealerInfo?.logoUri}
                      alt="Logo"
                      style={{ maxWidth: '150px', height: 'auto', objectFit: 'contain', marginRight: '10px' }}
                    />
                  )}
                </Col>
              </Row>

              <hr />

              <h6 className="mb-2">Inspection score</h6>
              <ScoreLegend passed={score.passed} attention={score.attention} urgent={score.urgent} />
            </Card.Body>
          </Card>

          {inspection.dealerWebsiteLink &&
            <Card className="mb-3">
              <Card.Body>
                <Button
                  variant="link"
                  onClick={() => window.open(inspection.dealerWebsiteLink, '_blank')}
                >
                  View Listing on Dealer Website
                </Button>
              </Card.Body>
            </Card>
          }

          {/* Reports */}
          {inspection.vehicleHistoryReportUrl &&
            <Row className="print-hidden">
              <Col>
                <Card className="mb-4">
                  <Card.Header className="fw-semibold">Vehicle History Report</Card.Header>
                  <Card.Body>
                    <Button
                      variant="link"
                      onClick={() => window.open(inspection.vehicleHistoryReportUrl, '_blank')}
                    >
                      View Vehicle History Report (PDF)
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          }
          {inspection.vehicleServiceRecordUrl &&
            <Row className="print-hidden">
              <Col>
                <Card className="mb-4">
                  <Card.Header className="fw-semibold">Vehicle Service Record</Card.Header>
                  <Card.Body>
                    <Button
                      variant="link"
                      onClick={() => window.open(inspection.vehicleServiceRecordUrl, '_blank')}
                    >
                      View Vehicle Service Record (PDF)
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          }

          <Card>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0">Categories</h6>
                <Form.Check
                  type="switch"
                  id="cat-filter-switch"
                  className="d-lg-none"
                  label="Filter"
                  checked={activeCategoryId !== 'all'}
                  onChange={() =>
                    setActiveCategoryId(activeCategoryId === 'all'
                      ? (derivedCategories[0]?.id ?? 'all')
                      : 'all')
                  }
                />
              </div>
              <ListGroup>
                <ListGroup.Item action active={activeCategoryId === 'all'} onClick={() => setActiveCategoryId('all')}>
                  All Items
                </ListGroup.Item>
                {derivedCategories.map(c => (
                  <ListGroup.Item
                    key={c.id}
                    action
                    active={activeCategoryId === c.id}
                    onClick={() => setActiveCategoryId(c.id)}
                  >
                    {c.name}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        {/* RIGHT: items */}
        <Col lg={8}>
          {filteredItems.length === 0 && (
            <Card className="avoid-break"><Card.Body><Empty text="No items in this category." /></Card.Body></Card>
          )}

          {filteredItems.map((it) => (
            <Card className="mb-3 avoid-break print-item" key={it.id}>
              <Card.Body className="pb-2">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    {it.categoryName && <div className="text-muted small item-category">{it.categoryName}</div>}
                    <h5 className="mb-1 item-title d-flex align-items-center flex-wrap">
                      {it.displayOrder ? (
                        <span className="text-muted me-1">{it.displayOrder}.</span>
                      ) : null}

                      <span>{it.name}</span>

                      {statusBadge(it.status, it.statusLabelGreen, it.statusLabelYellow, it.statusLabelRed)}

                      {it.description && it.description.trim() && (
                        <OverlayTrigger
                          trigger="click"
                          placement="right"
                          rootClose
                          overlay={
                            <Tooltip id={`item-desc-${it.id}`}>
                              <div style={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>
                                {it.description}
                              </div>
                            </Tooltip>
                          }
                        >
                          <button
                            type="button"
                            className="btn btn-link p-0 ms-2 item-help-icon"
                            aria-label="What does this inspection item mean?"
                          >
                            <FontAwesomeIcon icon={faCircleQuestion} />
                          </button>
                        </OverlayTrigger>
                      )}
                    </h5>

                  </div>
                </div>

                <Row className="mt-2 g-3 align-items-stretch">
                  <Col md={7} className="print-col-left">
                    <ListGroup variant="flush" className="item-list">
                      <ListGroup.Item className="px-0">
                        <div className="fw-semibold">Technician Note</div>
                        {it.technicianNote ? <div>{it.technicianNote}</div> : <Empty text="No note provided." />}
                      </ListGroup.Item>
                      {it.recommendation && (
                        <ListGroup.Item className="px-0">
                          <div className="fw-semibold">Recommendation</div>
                          <div>{it.recommendation}</div>
                        </ListGroup.Item>
                      )}
                    </ListGroup>
                  </Col>

                  <Col md={5} className="print-col-right">
                    {/* Photos */}
                    <div className="d-flex flex-wrap gap-2 justify-content-start item-photos">
                      {it.photos && it.photos.length > 0 ? (
                        it.photos.map((p: PhotoDto) => (
                          <Button
                            key={`p-${p.id}`}
                            variant="light"
                            className="p-0 border photo-thumb print-photo-thumb"
                            style={{ width: 112, height: 80, overflow: 'hidden' }}
                            onClick={() => setPhotoModal({ open: true, url: p.url })}
                            title="View photo"
                          >
                            <img
                              src={p.thumbnailUrl ?? p.url}
                              alt="thumbnail"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              loading="lazy"
                            />
                          </Button>
                        ))
                      ) : (
                        <div className="text-muted small d-inline-flex align-items-center">
                          <FontAwesomeIcon icon={faCamera} className="me-2" />
                          No photos
                        </div>
                      )}
                    </div>

                    {/* Videos */}
                    <div className="d-flex flex-wrap gap-2 justify-content-start item-videos mt-2">
                      {it.videos && it.videos.length > 0 ? (
                        it.videos.map((v: any) => (
                          <button
                            type="button"
                            key={`v-${v.id}`}
                            className="video-thumb-btn"
                            style={{ width: 112, height: 80 }}
                            onClick={() => setVideoModal({ open: true, url: v.url, poster: v.thumbnailUrl ?? null })}
                            title="Play video"
                            aria-label="Play video"
                          >
                            {v.thumbnailUrl ? (
                              <img src={v.thumbnailUrl} alt="" loading="lazy" />
                            ) : (
                              <div className="video-thumb-empty">
                                <span className="video-label">Video</span>
                              </div>
                            )}
                            <span className="play-overlay" aria-hidden />
                          </button>
                        ))
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ))}

          {/* Additional Comments */}
          {inspection.notes !== undefined &&
            <Card className="mb-4 avoid-break">
              <Card.Header className="fw-semibold">Additional Comments</Card.Header>
              <Card.Body>
                {inspection.notes && inspection.notes.trim() ? (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{inspection.notes}</div>
                ) : (
                  <div className="text-muted">None provided.</div>
                )}
              </Card.Body>
            </Card>
          }
        </Col>
      </Row>

      {/* Floating "Questions?" banner (only when modal is not open) */}
      {!showLeadModal && (
        <button
          type="button"
          className="lead-float-btn print-hidden"
          onClick={openLeadModal}
          aria-label="Questions? Open contact form"
          title="Questions?"
        >
          Questions?
        </button>
      )}

      {/* Lead Modal (popup after 10 seconds + manual open) */}
      <Modal
        show={showLeadModal}
        onHide={closeLeadModal}
        centered
        dialogClassName="lead-modal-dialog"
        contentClassName="lead-modal-content"
        backdrop
        keyboard
      >
        <Modal.Header className="lead-modal-header">
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="pe-2">
              <div className="fw-semibold">Have questions? Get in touch</div>
              <div className="text-muted small">We’ll follow up shortly.</div>
            </div>
            <Button
              variant="link"
              className="p-0 lead-modal-close"
              onClick={closeLeadModal}
              aria-label="Close"
              title="Close"
            >
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="lead-modal-body">
          {renderLeadForm(true)}
        </Modal.Body>
      </Modal>

      {/* Lead Form (screen only) - still present at bottom */}
      <Row className="mt-3 print-hidden">
        <Col lg={{ span: 8, offset: 4 }}>
          <Card className="mb-4">
            <Card.Header className="fw-semibold">Have questions? Get in touch</Card.Header>
            <Card.Body>
              {renderLeadForm(false)}
            </Card.Body>
          </Card>
        </Col>
        <div className='mb-3 text-center'>
          <a href='get-started'>Dealer? Sign up Q-caR and get started today!</a>
        </div>
      </Row>

      {/* Photo modal */}
      <Modal show={photoModal.open} onHide={() => setPhotoModal({ open: false })} size="lg" centered>
        <Modal.Body className="p-0">
          {photoModal.url && (
            <Image src={photoModal.url} alt="Inspection photo" fluid style={{ width: '100%', height: 'auto' }} />
          )}
        </Modal.Body>
      </Modal>

      {/* Video modal */}
      <Modal show={videoModal.open} onHide={() => setVideoModal({ open: false })} size="lg" centered>
        <Modal.Body className="p-0">
          {videoModal.url && (
            <video
              src={videoModal.url}
              poster={videoModal.poster ?? undefined}
              style={{ width: '100%', height: 'auto', background: '#000' }}
              controls
              playsInline
              autoPlay
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Styles */}
      <style>{`
  /* ---------- Small helpers ---------- */
  @media (max-width: 767.98px) {
    .mobile-center {
      display: flex !important;
      flex-direction: column;
      align-items: center !important;
      text-align: center !important;
    }
  }

  /* ---------- Floating Questions button ---------- */
  .lead-float-btn {
    position: fixed;
    z-index: 1060; /* above content; below Bootstrap modals (usually 1055-1065) */
    border: 1px solid rgba(255,255,255,0.18);
    background: #82bb67;
    color: rgba(1, 1, 1, 0.92);
    padding: 10px 14px;
    border-radius: 999px;
    font-weight: 600;
    letter-spacing: 0.2px;
    box-shadow: 0 14px 40px rgba(0,0,0,0.45);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    cursor: pointer;
  }

  .lead-float-btn:hover {
    transform: translateY(-1px);
  }

  /* Desktop: top-right */
  @media (min-width: 576px) {
    .lead-float-btn {
      right: 18px;
      top: 18px;
    }
  }

  /* Mobile: centered-ish near bottom */
  @media (max-width: 575.98px) {
    .lead-float-btn {
      left: 50%;
      transform: translateX(-50%);
      bottom: 18px;
      width: calc(100vw - 36px);
      max-width: 420px;
      text-align: center;
    }
    .lead-float-btn:hover {
      transform: translateX(-50%) translateY(-1px);
    }
  }

  /* ---------- Lead modal (dark) ---------- */
  .lead-modal-dialog {
    width: auto !important;
    max-width: 680px !important;
    margin: 0.75rem auto;
  }

  .lead-modal-content {
    border-radius: 14px;
    overflow: hidden;
    background: #151a20 !important;
    color: rgba(255,255,255,0.92) !important;
    border: 1px solid rgba(255,255,255,0.10) !important;
    box-shadow: 0 20px 60px rgba(0,0,0,0.55);
  }

  .lead-modal-header {
    background: #151a20 !important;
    color: rgba(255,255,255,0.92) !important;
    border-bottom: 1px solid rgba(255,255,255,0.10) !important;
  }

  .lead-modal-body {
    background: #151a20 !important;
    color: rgba(255,255,255,0.92) !important;
  }

  .lead-modal-content .form-label,
  .lead-modal-content label,
  .lead-modal-content .form-check-label {
    color: rgba(255,255,255,0.82) !important;
  }

  .lead-modal-content .text-muted,
  .lead-modal-content .small,
  .lead-modal-content small {
    color: rgba(255,255,255,0.60) !important;
  }

  .lead-modal-content .form-control,
  .lead-modal-content textarea.form-control {
    background: #0f141a !important;
    color: rgba(255,255,255,0.92) !important;
    border: 1px solid rgba(255,255,255,0.14) !important;
  }

  .lead-modal-content .form-control::placeholder {
    color: rgba(255,255,255,0.45) !important;
  }

  .lead-modal-content .form-control:focus {
    border-color: rgba(255,255,255,0.28) !important;
    box-shadow: 0 0 0 0.2rem rgba(255,255,255,0.08) !important;
  }

  .lead-modal-close {
    color: rgba(255,255,255,0.70) !important;
    text-decoration: none;
    font-size: 1.25rem;
    line-height: 1;
  }
  .lead-modal-close:hover { color: rgba(255,255,255,0.95) !important; }

  /* Mobile popup: not full height, not overflowing */
  @media (max-width: 575.98px) {
    .lead-modal-dialog {
      width: auto !important;
      max-width: calc(100vw - 28px) !important; /* matches margin: 14px */
      margin: 14px !important;
      box-sizing: border-box;
    }

    .lead-modal-content {
      width: 100% !important;
      max-width: 100% !important;
      max-height: 84vh;
      border-radius: 14px;
      box-sizing: border-box;
    }

    .lead-modal-body {
      max-height: calc(84vh - 64px);
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      padding-bottom: 18px;
    }

    /* Bootstrap sometimes adds padding-right for scrollbar */
    .modal { padding-right: 0 !important; }

    .modal-backdrop.show { opacity: 0.45; }
    .modal {
      backdrop-filter: blur(2px);
      -webkit-backdrop-filter: blur(2px);
    }
  }

  /* Extra safety: if any child tries to force width */
  .lead-modal-content * { max-width: 100%; }

  /* ---------- Screen-only helpers ---------- */
  @media screen {
    .print-only { display: none; }
  }

  /* ---------- Video thumbnails (SCREEN) ---------- */
  .item-videos { display: flex; flex-wrap: wrap; gap: 8px; }

  .video-thumb-btn {
    position: relative;
    display: inline-block;
    flex: 0 0 auto;
    width: 112px;
    height: 80px;
    overflow: hidden;
    padding: 0;
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 6px;
    background: #0f1115;
    cursor: pointer;
  }

  .video-thumb-btn img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-thumb-btn::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,.35));
    pointer-events: none;
    z-index: 1;
  }

  .play-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 3;
  }

  .play-overlay::before {
    content: "";
    display: block;
    width: 28px;
    height: 28px;
    background: #fff;
    clip-path: polygon(22% 15%, 85% 50%, 22% 85%);
    filter: drop-shadow(0 1px 2px rgba(0,0,0,.5));
    opacity: .95;
    border-radius: 1px;
  }

  .video-thumb-empty {
    position: relative;
    width: 100%;
    height: 100%;
    background: #0f1115;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255,255,255,0.9);
    font-size: 14px;
    letter-spacing: .2px;
  }

  .video-thumb-empty .video-label {
    position: absolute;
    bottom: 6px;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 12px;
    color: rgba(255,255,255,0.85);
    z-index: 2;
  }

  /* ---------- Item help icon ---------- */
  .item-help-icon {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    font-size: 0.9rem;
  }

  .item-help-icon:hover,
  .item-help-icon:focus {
    color: #ffffff;
    text-decoration: none;
  }

  @media print {
    .item-help-icon {
      display: none !important;
    }
  }

  /* ---------- Print: force light theme & compact layout ---------- */
  @media print {
    :root { -webkit-print-color-adjust: economy; print-color-adjust: economy; }

    html, body { background: #fff !important; color: #000 !important; }

    .container, .row, .col,
    .card, .card-body, .card-header, .list-group, .list-group-item,
    .modal-content, .bg-light, .offcanvas, .dropdown-menu {
      background: #fff !important;
      color: #000 !important;
      box-shadow: none !important;
    }

    .text-muted, .small, small { color: #000 !important; }
    .print-hidden { display: none !important; }

    .container { max-width: 100% !important; }
    .screen-grid { display: block !important; }
    .card { border: 1px solid #C9CDD1 !important; }
    .card .card-body { padding: 10pt 12pt !important; }
    .avoid-break { break-inside: avoid; page-break-inside: avoid; }
    h2, h3, h4, h5 { page-break-after: avoid; }

    .print-only { display: block !important; }
    .print-header { border: 1px solid #C9CDD1; padding: 12pt; background: #fff !important; }
    .print-logo { height: 28pt; display: block; margin-bottom: 6pt; }

    .print-vehicle-photo .photo-wrap {
      width: 100%;
      aspect-ratio: 4 / 3;
      border: 1px solid #C9CDD1;
      overflow: hidden;
      border-radius: 6pt;
      background: #fff !important;
    }
    .print-vehicle-photo img { width: 100%; height: 100%; object-fit: cover; }

  .badge, .status-badge {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Force Bootstrap badge colors in print */
  .badge.bg-success, .status-badge.bg-success {
    background-color: var(--bs-success) !important;
    color: #fff !important;
  }

  .badge.bg-warning, .status-badge.bg-warning {
    background-color: var(--bs-warning) !important;
    color: #000 !important; /* warning is light */
  }

  .badge.bg-danger, .status-badge.bg-danger {
    background-color: var(--bs-danger) !important;
    color: #fff !important;
  }

  .badge.bg-secondary, .status-badge.bg-secondary {
    background-color: var(--bs-secondary) !important;
    color: #fff !important;
  }

    .score-legend .list-group-item { border: none !important; padding: 2pt 0 !important; }
    .legend-icon { color: #000 !important; }

    .print-category { border-bottom: 1px solid #C9CDD1; padding-bottom: 4pt; margin-top: 16pt; }
    .print-item  { margin-bottom: 10pt !important; }
    .item-category { font-size: 9pt; }

    .item-photos { gap: 4pt !important; }
    .print-photo-thumb { border: 1px solid #C9CDD1 !important; background: #fff !important; }
    .print-photo-thumb img { width: 140pt; height: 95pt; object-fit: cover; }

    h5 { margin: 0 0 4pt 0 !important; }
    .fw-semibold { font-weight: 600 !important; }
    .list-group-item { border: none !important; padding-left: 0 !important; padding-right: 0 !important; }
    .item-list .list-group-item + .list-group-item { margin-top: 2pt; }

    .print-link { word-break: break-all; text-decoration: none; color: #000 !important; }

    .print-col-left, .print-col-right { width: 50% !important; float: left !important; }
    .print-col-right { padding-left: 8pt; }

    .item-videos { display: none !important; }
  }
`}</style>

    </div>
  );
};

export default DviViewer;
